import { useCallback, useRef } from "react";
import { IMAGE_ZOOM } from "../const";

interface BackgroundImageData {
  src: string;
  width?: number;
  height?: number;
}

const IMAGE_Y_OFFSET = 120;

const loadImage = (src: string, signal?: AbortSignal) =>
  new Promise<HTMLImageElement>((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    const abortHandler = () => {
      img.src = "";
      rej(new Error("AbortError"));
    };

    if (signal) {
      signal.addEventListener("abort", abortHandler);
    }

    img.onload = () => {
      if (signal?.aborted) {
        rej(new Error("AbortError"));
        return;
      }
      if (signal) {
        signal.removeEventListener("abort", abortHandler);
      }
      res(img);
    };

    img.onerror = (error) => {
      if (signal) {
        signal.removeEventListener("abort", abortHandler);
      }
      rej(error);
    };

    img.src = src;
  });

export const useBackgroundImage = () => {
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map());

  const drawBackgroundImage = useCallback(
    async (
      ctx: CanvasRenderingContext2D,
      data: BackgroundImageData,
      canvasWidth: number,
      signal?: AbortSignal,
    ): Promise<void> => {
      try {
        if (signal?.aborted) {
          throw new Error("AbortError");
        }

        // 画像の読み込み
        let image: HTMLImageElement;
        const cachedImage = imageCache.current.get(data.src);

        if (cachedImage) {
          image = cachedImage;
        } else {
          image = await loadImage(data.src, signal);
          if (signal?.aborted) {
            throw new Error("AbortError");
          }
          imageCache.current.set(data.src, image);
        }

        // 描画サイズの計算
        const aspectRatio = image.width / image.height;
        const targetWidth = canvasWidth * IMAGE_ZOOM;
        const targetHeight = targetWidth / aspectRatio;
        const x = (canvasWidth - targetWidth) / 2;
        const canvasHeight = ctx.canvas.height;
        const y = (canvasHeight - targetHeight) / 2 + IMAGE_Y_OFFSET;

        // 背景を白で塗りつぶす
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // 画像を描画
        ctx.drawImage(image, x, y, targetWidth, targetHeight);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }
        console.error("背景画像の描画中にエラーが発生しました:", error);
        throw error;
      }
    },
    [],
  );

  return {
    drawBackgroundImage,
  };
};
